/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { Provider } from "@/components/email-provider-selection";
import { AuthenticatedHttpClient } from "@/lib/axios";
import { AuthProvider } from "@/types/common";
import {
  BulkPlacementReportStatusItem,
  DetailedProviderBreakdownResponse,
  EmailDetailResponse,
  EmailProvider,
  FormattedTestResultsResponse,
  InboxPlacementApiResponse,
  PlacementDataResponse,
  PlacementType,
  TestHistoryResponse,
  // You may need to add a type for provider breakdown response
  // You may need to add a type for email details response
  // You may need to add a type for test history response
} from "@/types/inboxPlacements.types";
import { DateRange } from "react-day-picker";

export class InboxPlacementService {
  private http: AuthenticatedHttpClient;
  private baseUrl = "/inbox-placement";

  constructor(http: AuthenticatedHttpClient) {
    this.http = http;
  }

  async getInboxPlacements(params?: {
    type?: PlacementType;
    page?: number;
    limit?: number;
  }) {
    const response = await this.http.get<InboxPlacementApiResponse>(
      `${this.baseUrl}/summary/grid?type=${params?.type}&page=${params?.page}&limit=${params?.limit}`
    );
    return response as unknown as InboxPlacementApiResponse;
  }

  async getCardSummary(type: PlacementType) {
    const response = await this.http.get<PlacementDataResponse>(
      `${this.baseUrl}/card/summary?type=${type}`
    );
    return response as unknown as PlacementDataResponse;
  }

  async getTestResult(email: string, type: PlacementType, testId: string) {
    const response = await this.http.get(
      `${this.baseUrl}/test-result/${encodeURIComponent(
        email
      )}?type=${type}&testId=${testId}`
    );
    // @ts-expect-error
    return response.result as unknown as FormattedTestResultsResponse;
  }

  async getProviderBreakdown(email: string, testId: string) {
    const response = await this.http.get(
      `${this.baseUrl}/provider-breakdown/${encodeURIComponent(
        email
      )}/${encodeURIComponent(testId)}`
    );
    // Return the response data - you may want to add a proper type here
    return response as unknown as DetailedProviderBreakdownResponse;
  }

  async getEmailDetails(testId: string, provider: string, email: string) {
    const response = await this.http.get(
      `${this.baseUrl}/email-details/${encodeURIComponent(
        testId
      )}/${encodeURIComponent(provider)}/${encodeURIComponent(email)}`
    );
    // You may want to add a proper type here
    // @ts-ignore
    return response?.emailDetails as unknown as EmailDetailResponse;
  }

  async getTestHistory(email: string, dateRange: DateRange, page: number, limit: number, type: string) {
    const response = await this.http.get(
      `${this.baseUrl}/test-history?email=${encodeURIComponent(
        email
      )}&page=${page}&limit=${limit}&type=${type}&startDate=${dateRange.from?.toISOString()}&endDate=${dateRange.to?.toISOString()}`
    );
    // You may want to add a proper type here for the test history response
    return response as unknown as TestHistoryResponse;
  }

  async getWarmups() {
    const response = await this.http.get(`/inbox-placement/warmups`);
    return response as unknown as any[];
  }

  async getMailbox(email?: string, espType?: AuthProvider) {
    const url = email
      ? `/inbox-placement/mailbox?email=${email}&espType=${espType}`
      : `/inbox-placement/mailbox?espType=${espType}`;
    const response = await this.http.get(url);
    return response as unknown as any[];
  }

  async getTemplate(templateSearchTerm?: string) {
    const url = templateSearchTerm
      ? `/inbox-placement/template?template=${templateSearchTerm}`
      : `/inbox-placement/template`;
    const response = await this.http.get(url);
    return response as unknown as any[];
  }

  async getPlacementAccounts(type: string) {
    const response = await this.http.get(
      `/inbox-placement/placement-accounts?type=${type}`
    );
    return response as unknown as Array<{
      _id: string;
      email: string;
      provider: EmailProvider;
    }>;
  }

  async runPlacementTestsender(
    senderEmail: string,
    recipientType: string,
    testEmails: string[],
    provider: string[]
  ) {
    const response = await this.http.post(`/inbox-placement/test-sender`, {
      senderEmail,
      recipientType,
      testEmails,
      provider,
    });
    return response as unknown as any[];
  }

  async createScheduledTest(
    mailbox: string[],
    template: string,
    providers: string[],
    type: string,
    espType: AuthProvider,
    {
      type: scheduleType,
      recurrenceType,
      startTime,
      customRecurrence,
    }: {
      type: string;
      recurrenceType: string;
      startTime: Date;
      customRecurrence: any;
    }
  ) {
    const response = await this.http.post(`/inbox-placement/scheduled-test`, {
      mailbox,
      template,
      type,
      providers,
      espType,
      schedule: { scheduleType, recurrenceType, startTime, customRecurrence },
    });
    return response as unknown as any[];
  }

  async deleteScheduledTest(id: string) {
    const response = await this.http.delete(
      `/inbox-placement/scheduled-tests/${id}`
    );
    return response as unknown as any[];
  }

  async updateScheduledTest(
    id: string,
    mailbox: string[],
    template: string,
    type: string,
    {
      scheduleType,
      recurrenceType,
      startTime,
      customRecurrence,
    }: {
      scheduleType: string;
      recurrenceType: string;
      startTime: Date;
      customRecurrence: any;
    }
  ) {
    const response = await this.http.post(
      `/inbox-placement/scheduled-test/${id}`,
      {
        mailbox,
        template,
        type,
        schedule: { scheduleType, recurrenceType, startTime, customRecurrence },
      }
    );
    return response as unknown as any[];
  }

  async getScheduledTests() {
    const response = await this.http.get(`/inbox-placement/scheduled-tests`);
    return response as unknown as any[];
  }

  async runPlacementTest(
    senderEmail: string,
    testType: string,
    provider: string[],
    espType: AuthProvider
  ) {
    const response = await this.http.post(`/inbox-placement/test`, {
      senderEmail,
      testType,
      provider,
      espType,
    });
    return response as unknown as any[];
  }

  async sendTestEmail(
    senderEmail: string,
    template: string,
    recipientType: string,
    espType: AuthProvider
  ) {
    const response = await this.http.post(`/inbox-placement/test-email`, {
      senderEmail,
      template,
      recipientType,
      espType,
    });
    return response as unknown as any[];
  }

  async exportTestHistory(email: string, page: number, limit: number, type: string, startDate?: string, endDate?: string) {
    return await this.http.get(
      `/inbox-placement/export-test-history?email=${email}&page=${page}&limit=${limit}&type=${type}&startDate=${startDate}&endDate=${endDate}`,
      {
        responseType: "blob",
      }
    );
  }

  async exportPlacementSummaryGrid(type: string, email?: string) {
    return await this.http.get(
      `/inbox-placement/export-summary/grid?type=${type}&email=${email}`,
      {
        responseType: "blob",
      }
    );
  }

  async checkPlacementTestExists(email: string) {
    const response = await this.http.get(`/public-result/test-exist/${email}`);
    return response as unknown as {
      success: boolean;
      message: string;
      testData: {
        testId: string;
        createdAt: string;
      };
    };
  }

  // ------------------------- Publish Report -------------------------

  async publishInboxPlacementReport(ids: string[]) {
    const response = await this.http.post(
      "/inbox-placement/placement-result/publish",
      {
        ids,
      }
    );
    return response as unknown as string[];
  }

  async unPublishInboxPlacementReport(ids: string[]) {
    const response = await this.http.post(
      "/inbox-placement/placement-result/unpublish",
      {
        ids,
      }
    );
    return response as unknown as boolean;
  }

  async publishAllInboxPlacementReport(emails: string[], type: string) {
    const response = await this.http.post(
      "/inbox-placement/placement-result/publish-all",
      {
        emails,
        type,
      }
    );
    return response as unknown as string[];
  }

  async unPublishAllInboxPlacementReport(email: string[], type: string) {
    const response = await this.http.post(
      "/inbox-placement/placement-result/unpublish-all",
      {
        email,
        type,
      }
    );
    return response as unknown as boolean;
  }

  async checkBulkPlacementReportStatus(testIds: string[]) {
    const response = await this.http.get("/inbox-placement/multi-test-status", {
      testIds: testIds.join(","),
    });
    return response as unknown as BulkPlacementReportStatusItem[];
  }
}
